/**
 * CommunityProjectsPage — "All projects in <community>" view.
 *
 * Destination for the "View all projects →" link on the Alignment tab.
 * Renders every visible project for the community as a card grid (reuses
 * ProjectRow with `maxVisible={0}` for unlimited rendering).
 *
 * Visibility filtering happens at the project layer — only projects the
 * current user can view are returned by fetchProjectsByCommunity.
 *
 * DEV-970 — minimal landing page for the "show all projects" link;
 * intentionally lean so the demo timeline works without rebuilding the
 * card layer.
 */

import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import ProjectRow from '../../Components/ProjectRow/ProjectRow';
import { useApiCommunityData } from '../../api/useApiCommunityData';
import { communityConfigStore } from '../../Data/store';
import styles from './CommunityProjectsPage.module.css';

type CommunityEntity = {
  entity_guid: string;
  name?: string;
  entity_type?: string;
  code?: string;
};

/** Resolve the country flag emoji from an ISO 3166-1 alpha-2 code. */
function countryFlagEmoji(code: string | null | undefined): string {
  if (!code || code.length < 2) return '';
  const upper = code.toUpperCase().trim();
  const cc = upper.indexOf('-') === 2 ? upper.slice(0, 2) : (upper.length === 2 ? upper : '');
  if (!cc || !/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...Array.from(cc).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/**
 * Walk the parent map up to the geo_country ancestor, returning the
 * flag emoji and the local entity name.
 */
function resolveCountryFor(
  entityGuid: string,
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
): { flag: string; name: string } {
  if (!entityGuid) return { flag: '', name: '' };
  const startGuid = entityGuid.toUpperCase();
  const startEntity = entityMap.get(startGuid);
  const localName = startEntity ? String(startEntity.name || '') : '';
  let guid: string | undefined = startGuid;
  for (let i = 0; i < 6 && guid; i++) {
    const entity = entityMap.get(guid);
    if (!entity) break;
    const etype = String(entity.entity_type || '').toLowerCase();
    if (etype === 'geo_country' && entity.code) {
      return { flag: countryFlagEmoji(entity.code), name: localName || String(entity.name || entity.code) };
    }
    guid = parentMap.get(guid);
  }
  return { flag: '', name: localName };
}

const CommunityProjectsPage: React.FC = () => {
  const { id: communityGuid } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useAppTranslation('pages');

  const apiCommunityData = useApiCommunityData();

  // Resolve the community name from the store first (snappiest), then
  // the live API as a fallback. Identical to the resolution path in
  // CommunityDetailPage.
  const community = useMemo<CommunityEntity | undefined>(() => {
    if (!communityGuid) return undefined;
    const normGuid = String(communityGuid).toUpperCase();
    const storeEntry = communityConfigStore.get(normGuid);
    if (storeEntry) {
      return {
        entity_guid: communityGuid,
        name: storeEntry.name,
        entity_type: storeEntry.entity_type,
      };
    }
    const apiHit = (apiCommunityData.communityEntities || []).find(
      (e: any) => String(e.entity_guid).toUpperCase() === normGuid,
    );
    if (apiHit) {
      return {
        entity_guid: apiHit.entity_guid,
        name: apiHit.name,
        entity_type: apiHit.entity_type,
        code: apiHit.code,
      } as CommunityEntity;
    }
    return undefined;
  }, [communityGuid, apiCommunityData.communityEntities]);

  const name = community?.name || t('communityProjects.fallbackTitle');

  const entityMap = useMemo(() => {
    const m = new Map<string, CommunityEntity>();
    for (const e of (apiCommunityData.communityEntities || [])) {
      const key = String(e.entity_guid).toUpperCase();
      m.set(key, {
        entity_guid: key,
        name: e.name,
        entity_type: e.entity_type,
        code: e.code,
      });
    }
    return m;
  }, [apiCommunityData.communityEntities]);

  const parentMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of (apiCommunityData.communityRelationships || [])) {
      m.set(String(r.child_entity_guid).toUpperCase(), String(r.parent_entity_guid).toUpperCase());
    }
    return m;
  }, [apiCommunityData.communityRelationships]);

  const countryInfo = useMemo(
    () => resolveCountryFor(communityGuid || '', entityMap, parentMap),
    [communityGuid, entityMap, parentMap],
  );

  if (!communityGuid) return null;

  return (
    <div className={styles.page}>
      {/* DEV-1452: in-app back button removed. */}
      <div className={styles.header}>
        <div className={styles.title}>
          {countryInfo.flag && <span className={styles.flag}>{countryInfo.flag}</span>}
          <span>{t('communityProjects.title', { community: name })}</span>
        </div>
      </div>
      <div className={styles.body}>
        <ProjectRow
          communityGuid={communityGuid}
          maxVisible={0}
          sectionTitle={t('communityProjects.allProjectsSection')}
          countryFlag={countryInfo.flag}
          countryName={countryInfo.name}
          onOpen={(projectGuid) => {
            navigate(`/projects/${projectGuid}`, {
              state: {
                // DEV-1452: returnTo no longer feeds a back button (removed).
                // It still feeds ProjectDetailPage's close/restore handlers.
                returnTo: `/communities/${communityGuid}/projects`,
                viewMode: 'member',
              },
            });
          }}
        />
      </div>
    </div>
  );
};

export default CommunityProjectsPage;
